import * as React from 'react'
import { Container } from '../components/container'
import { useStyles, useTheme } from '@conte-ltd/components-theme'
import { Theme } from '../styles/theme'
import { useMediaQuery, useDidUpdateEffect } from '@conte-ltd/components-utils'
import { css } from '@emotion/react'
import {
  Form,
  FormControl,
  Label,
  LabelProps,
  TextField,
  useFormContext,
  useFormControl,
} from '@conte-ltd/components-form'
import { Separator } from '@conte-ltd/components-separator'
import { button, Button } from '../components/button'
import { navigate } from 'gatsby-link'
import axios from 'axios'
import { useState } from 'react'
import sanitizeHtml from 'sanitize-html'
import { Spinner } from '../components/spinner'
import { Layout } from '../components/layout'
import { Section } from '../components/section'

const URL = '/api/send-mail'

type FormProgress = 'input' | 'confirm' | 'complete' | 'error'

const FormLabel = (props: LabelProps) => {
  const { margin, fontSize } = useStyles<Theme>()
  const { isRequired } = useFormControl()
  const { children } = props
  return (
    <Label {...props}>
      {children}
      {isRequired && <span css={[margin.left.offset.xs, fontSize.xs]}>＊</span>}
    </Label>
  )
}

const FormConfirmText = ({
  name,
  progress,
}: {
  name: string
  progress: FormProgress
}) => {
  const { lineHeight, margin } = useStyles<Theme>()
  const { getValues } = useFormContext()
  const value = getValues(name)
  return (
    <>
      {progress === 'confirm' && (
        <p
          css={[margin.top.offset.negative['2xs'], lineHeight.normal]}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(value ?? '').replace(/\r?\n/g, '<br />'),
          }}
        ></p>
      )}
    </>
  )
}

const ContactPage = () => {
  const {
    styles: {
      margin,
      padding,
      height,
      display,
      flexDirection,
      justifyContent,
      justifyItems,
      alignItems,
      gap,
      grid,
      fontSize,
      fontWeight,
      letterSpacing,
      lineHeight,
      textAlign,
      whiteSpace,
      resize,
      order,
    },
    variables: vars,
    breakpoints: bp,
  } = useTheme<Theme>()
  const [progress, setProgress] = useState<FormProgress>('input')
  const isMobile = useMediaQuery(bp.lg)
  const isDesktop = !isMobile

  async function sendMail(content: Object) {
    switch (progress) {
      case 'input':
        setProgress('confirm')
        break
      case 'confirm':
        try {
          const data = Object.entries(content).reduce((prev, [k, v]) => {
            return {
              ...prev,
              [k]: encodeURIComponent(v?.replace(/\r?\n/g, '\n') ?? ''),
            }
          }, {})

          await axios({
            method: 'post',
            url: URL,
            headers: {
              'Content-Type': 'application/json;charset=utf-8',
            },
            data,
          })

          setProgress('complete')
        } catch (err) {
          console.log((err as Error).message)
          setProgress('error')
        }

        break
      case 'complete':
        setProgress('input')

      default:
        break
    }
  }

  useDidUpdateEffect(() => {
    navigate('#contact')
  }, [progress])

  return (
    <Layout>
      <Section id={'contact'} className={'light-mode'}>
        <Container
          css={[
            isMobile ? padding.y['4xl'] : padding.y['5xl'],
            display.flex,
            flexDirection.column,
          ]}
        >
          <h1
            css={[isMobile ? fontSize.xl : fontSize['4xl'], textAlign.center]}
          >
            CONTACT
          </h1>

          <h2
            css={[
              isMobile ? fontSize.sm : fontSize.md,
              fontWeight.medium,
              letterSpacing.tighter,
              textAlign.center,
              isMobile ? margin.top['2xs'] : margin.top.sm,
            ]}
          >
            お問い合わせ
          </h2>

          {progress === 'input' ? (
            <p css={[margin.top.lg, textAlign.center, whiteSpace.nowrap]}>
              ホームページ制作やリニューアル、
              {isMobile && <br />}
              各種デザイン、映像制作の
              {isDesktop && <br />}
              ご依頼・ご相談は
              {isMobile && <br />}
              こちらからお問い合わせください。
              <br />
              お問い合せ内容の確認後、担当者より
              {isMobile && <br />}
              ご連絡させていただきます。
            </p>
          ) : progress === 'confirm' ? (
            <p css={[margin.top.lg, textAlign.center, whiteSpace.nowrap]}>
              まだ送信完了していません。
              <br />
              記入内容をご確認の上、送信して下さい。
            </p>
          ) : progress === 'complete' ? (
            <p css={[margin.top.lg, textAlign.center, whiteSpace.nowrap]}>
              この度はお問い合わせいただき、
              {isMobile && <br />}
              誠にありがとうございます。
              <br />
              3営業日以内に返信が来ない場合、
              <br />
              大変お手数ですがinfo@conte.ltdまで
              {isMobile && <br />}
              直接ご連絡ください。
            </p>
          ) : (
            <p css={[margin.top.lg, textAlign.center, whiteSpace.nowrap]}>
              大変申し訳ございません。
              <br />
              メールの送信に失敗しましたので、
              {isMobile && <br />}
              再度お問合せフォームから送信して下さい。
              <br />
              再度送信に失敗する場合は、
              {isMobile && <br />}
              大変お手数ですがinfo@conte.ltdまで
              {isMobile && <br />}
              直接ご連絡ください。
            </p>
          )}

          {progress === 'input' || progress === 'confirm' ? (
            <Form
              shouldUseNativeValidation={false}
              onSubmit={sendMail}
              css={[
                margin.top['2xl'],
                display.grid,
                justifyItems.stretch,
                isMobile ? gap.all.xs : gap.all.sm,
                isDesktop &&
                  css`
                    grid-template-columns: ${vars.size[170]} 1fr;
                  `,
              ]}
            >
              {({ formState }) => {
                return (
                  <>
                    <FormControl css={display.contents} isRequired>
                      <FormLabel>お名前／漢字</FormLabel>
                      <TextField
                        type={progress === 'confirm' ? 'hidden' : 'text'}
                        name={'name_kanji'}
                        placeholder={'例：山田太郎'}
                        required
                      />
                      <FormConfirmText
                        progress={progress}
                        name={'name_kanji'}
                      />
                    </FormControl>

                    <Separator
                      css={[
                        isDesktop && grid.column.start[2],
                        margin.bottom['3xs'],
                      ]}
                    />

                    <FormControl css={display.contents} isRequired>
                      <FormLabel>お名前／カナ</FormLabel>
                      <TextField
                        type={progress === 'confirm' ? 'hidden' : 'text'}
                        name={'name_kana'}
                        placeholder={'例：ヤマダタロウ'}
                        required
                      />
                      <FormConfirmText progress={progress} name={'name_kana'} />
                    </FormControl>

                    <Separator
                      css={[
                        isDesktop && grid.column.start[2],
                        margin.bottom['3xs'],
                      ]}
                    />

                    <FormControl css={display.contents} isRequired>
                      <FormLabel>メールアドレス</FormLabel>
                      <TextField
                        type={progress === 'confirm' ? 'hidden' : 'email'}
                        name={'email'}
                        placeholder={'例：info@conte.ltd'}
                        required
                      />
                      <FormConfirmText progress={progress} name={'email'} />
                    </FormControl>

                    <Separator
                      css={[
                        isDesktop && grid.column.start[2],
                        margin.bottom['3xs'],
                      ]}
                    />

                    <FormControl css={display.contents}>
                      <FormLabel>電話番号</FormLabel>
                      <TextField
                        type={progress === 'confirm' ? 'hidden' : 'tel'}
                        name={'tel'}
                        placeholder={'例：000-000-0000'}
                      />
                      <FormConfirmText progress={progress} name={'tel'} />
                    </FormControl>

                    <Separator
                      css={[
                        isDesktop && grid.column.start[2],
                        margin.bottom['3xs'],
                      ]}
                    />

                    <FormControl css={display.contents}>
                      <FormLabel>企業名・事業名</FormLabel>
                      <TextField
                        type={progress === 'confirm' ? 'hidden' : 'text'}
                        name={'group_name'}
                        placeholder={'例：株式会社CONTE'}
                      />
                      <FormConfirmText
                        progress={progress}
                        name={'group_name'}
                      />
                    </FormControl>

                    <Separator
                      css={[
                        isDesktop && grid.column.start[2],
                        margin.bottom['3xs'],
                      ]}
                    />

                    <FormControl css={display.contents} isRequired>
                      <FormLabel>お問い合わせ内容</FormLabel>
                      <TextField
                        as={'textarea'}
                        name={'details'}
                        placeholder={'お問い合わせ内容を入力ください'}
                        required
                        css={[
                          isMobile ? height[80] : height[130],
                          resize.none,
                          lineHeight.normal,
                          margin.top.offset.negative['2xs'],
                          progress === 'confirm' && display.none,
                        ]}
                      />
                      <FormConfirmText progress={progress} name={'details'} />
                    </FormControl>

                    <Separator
                      css={[
                        isDesktop && grid.column.start[2],
                        isMobile ? margin.bottom.md : margin.bottom.lg,
                      ]}
                    />

                    <div
                      css={[
                        isDesktop
                          ? [grid.column.span[2], justifyContent.flexEnd]
                          : [flexDirection.column, alignItems.center],
                        display.flex,
                        gap.all.sm,
                      ]}
                    >
                      {progress === 'confirm' && (
                        <Button
                          onPress={() => {
                            setProgress('input')
                          }}
                          css={[isMobile && order[1], button(vars.color).gray]}
                        >
                          BACK
                        </Button>
                      )}

                      <Button
                        type={'submit'}
                        css={[button(vars.color).black]}
                        isDisabled={formState.isSubmitting}
                      >
                        {formState.isSubmitting ? (
                          <Spinner />
                        ) : progress === 'confirm' ? (
                          'SEND MESSAGE'
                        ) : (
                          'CONFIRM'
                        )}
                      </Button>
                    </div>
                  </>
                )
              }}
            </Form>
          ) : (
            <div css={[textAlign.center, margin.top['2xl']]}>
              {progress === 'complete' ? (
                <Button href={'/'} css={[button(vars.color).gray]}>
                  BACK TO TOP
                </Button>
              ) : (
                <Button
                  onPress={() => {
                    setProgress('input')
                  }}
                  css={[button(vars.color).gray]}
                >
                  BACK
                </Button>
              )}
            </div>
          )}
        </Container>
      </Section>
    </Layout>
  )
}

export default ContactPage
